import { useContext } from "react";
import { useLocation } from "react-router-dom";

import { useOptionalAuthorization } from "@/contexts/AuthorizationContext";
import { useTenants } from "@/hooks/useTenants";
import { effectiveViewerControls } from "@/lib/dashboard/viewerControls";
import { DashboardRecordContext } from "@/pages/live/DashboardRecordContext";
import { isForcedAllTenantRoute } from "./tenantScopeRoute";

export type PagePreviewFilter = "tenants" | "time-range" | "collapse-all" | "events";

/**
 * Decides which scope controls (tenant, time range) belong to the current page.
 *
 * A dashboard declares its own viewer controls; other analytics routes fall back
 * to the route defaults. Pages outside analytics get no scope controls.
 */
export function usePageScopeControls(previewFilters?: readonly PagePreviewFilter[]) {
  const location = useLocation();
  const dashboardRecord = useContext(DashboardRecordContext);
  const monitorEntry = location.pathname === "/live/events/monitor";
  const protectionEntry = location.pathname === "/live/protection/events";
  const protectionPage = /^\/live\/protection\/events(?:\/|$)/.test(location.pathname);
  const campaignSchedulePage =
    /^\/live\/protection\/campaigns(?:\/|$)/.test(location.pathname) ||
    (dashboardRecord?.config.composition?.blocks?.length !== undefined &&
      dashboardRecord.config.composition.blocks.length > 0 &&
      dashboardRecord.config.composition.blocks.every(
        (block) => block.type === "campaign-schedule",
      ));
  const isClientProfile = useOptionalAuthorization()?.isClientProfile ?? false;
  const { data: authorizedTenants } = useTenants();

  const showAnalyticsScopeControls =
    location.pathname.startsWith("/live") || location.pathname.startsWith("/journal");
  const dashboardWidgets = dashboardRecord
    ? [
        ...(dashboardRecord.config.widgets ?? []),
        ...(dashboardRecord.config.sections ?? []).flatMap((section) => section.widgets),
      ]
    : [];
  const dashboardFilters =
    previewFilters ??
    (dashboardRecord
      ? effectiveViewerControls(dashboardRecord.config, dashboardWidgets)
      : ["tenants", "time-range"]);
  const hasDashboardFilters = previewFilters !== undefined || dashboardRecord !== null;
  const configuredTenantControl = hasDashboardFilters
    ? dashboardFilters.includes("tenants")
    : showAnalyticsScopeControls || location.pathname.startsWith("/measurement");
  const showTenant =
    configuredTenantControl &&
    !monitorEntry &&
    !protectionPage &&
    !/^\/live\/events\/[^/]+\/monitor\/?$/.test(location.pathname) &&
    (!isClientProfile || (authorizedTenants?.length ?? 0) > 1);
  const showTimeRange = hasDashboardFilters
    ? dashboardFilters.includes("time-range")
    : showAnalyticsScopeControls;
  const visibleTimeRange =
    showTimeRange && !monitorEntry && !protectionEntry && !campaignSchedulePage;
  const forceAllTenants = isForcedAllTenantRoute(location.pathname);
  // A dashboard that already renders its own event scope control — Event
  // Monitor/Overview via their composition context, Event Protection via its
  // own block — keeps owning that control; the generic one here is only for
  // a dashboard the user configured through the editor's own "Events" toggle.
  const hasOwnEventControl =
    Boolean(dashboardRecord?.config.composition?.context) ||
    (dashboardRecord?.config.composition?.blocks ?? []).some(
      (block) => block.type === "event-protection",
    );
  const showEvents =
    hasDashboardFilters &&
    (dashboardFilters.includes("events") ||
      dashboardRecord?.config.scope?.timeRange?.mode === "event-relative") &&
    !hasOwnEventControl;

  return {
    showTenant,
    showTimeRange: visibleTimeRange,
    showEvents,
    forceAllTenants,
    dashboardRecord,
    dashboardWidgets,
    dashboardFilters,
    authorizedTenants,
  };
}
